/**
 * F44 (Wave 2D) — admin financial dashboard API client.
 *
 * Endpoints (all under `/admin/financial`, gated by `financial:read`; the
 * export-generate + alert-rule PATCH additionally need `financial:export`):
 *   GET   /admin/financial/summary                  → getSummary            { data: FinanceSummary }
 *   GET   /admin/financial/settlements              → listSettlements       FinancePaginated<FinanceSettlementRow>
 *   GET   /admin/financial/refunds                  → listRefunds           FinancePaginated<FinanceRefundRow>
 *   GET   /admin/financial/receivables              → getReceivables        { data: FinanceReceivables }
 *   POST  /admin/financial/refresh                  → refresh               { data: FinanceRefreshResult }
 *   POST  /admin/financial/exports                  → generateExport        { data: FinanceExportJob }
 *   GET   /admin/financial/exports                  → listExportJobs        FinancePaginated<FinanceExportJob>
 *   GET   /admin/financial/exports/:exportId        → getExportJob          { data: FinanceExportJob }
 *   GET   /admin/financial/alert-rules              → listAlertRules        { data: FinanceAlertRule[] }
 *   PATCH /admin/financial/alert-rules/:ruleType    → updateAlertRule       { data: FinanceAlertRule }
 *
 * Response envelopes (frozen by @bravemove/types + the Wave-2A/2B/2C routes):
 *   - summary / receivables / export-detail / refresh / export-generate (201) → { data: <obj> }
 *   - settlements / refunds / export-history → FinancePaginated ({ data:[…], pagination:{…} })
 *   - alert-rules GET → { data: FinanceAlertRule[] }; PATCH → { data: FinanceAlertRule }
 *
 * Permission gating is server-side. The page-level <ProtectedRoute> gates which
 * routes an admin role can navigate to; the client forwards + relies on the
 * server to enforce RBAC. The vitest config aliases this module to
 * `financial.fixture` during tests (mirrors the F23 commission split).
 */
import type {
  ArAgingBucket,
  FinanceAlertRule,
  FinanceAlertType,
  FinanceExportJob,
  FinancePaginated,
  FinanceReceivables,
  FinanceRefundRow,
  FinanceRevenueSource,
  FinanceSettlementRow,
  FinanceStatementStatus,
  FinanceSummary,
  GenerateFinanceExportInput,
  RefundSourceKind,
  UpdateFinanceAlertRuleInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

// ─── Query option types ──────────────────────────────────────────────────────

export interface SummaryOpts {
  /** ISO `YYYY-MM-DD` (Asia/Beirut) inclusive lower bound. */
  readonly from: string;
  /** ISO `YYYY-MM-DD` (Asia/Beirut) inclusive upper bound. */
  readonly to: string;
  /** Compare-to-previous toggle — populates `previousMinor`/`deltaPercent` per KPI. */
  readonly compare?: boolean;
  readonly source?: FinanceRevenueSource;
}

export interface SettlementsOpts {
  readonly from: string;
  readonly to: string;
  readonly source?: FinanceRevenueSource;
  readonly status?: FinanceStatementStatus;
  readonly aging?: ArAgingBucket;
  readonly entityId?: string;
  readonly needsReconciliation?: boolean;
  readonly page?: number;
  readonly perPage?: number;
}

export interface RefundsOpts {
  readonly from: string;
  readonly to: string;
  readonly source?: RefundSourceKind;
  readonly page?: number;
  readonly perPage?: number;
}

export interface ReceivablesOpts {
  readonly asOf?: string;
}

export interface ExportHistoryOpts {
  readonly page?: number;
  readonly perPage?: number;
}

// ─── Query builders ──────────────────────────────────────────────────────────

function buildSummaryQuery(opts: SummaryOpts): string {
  return buildQuery({
    from: opts.from,
    to: opts.to,
    compare: opts.compare ? true : undefined,
    source: opts.source,
  });
}

function buildSettlementsQuery(opts: SettlementsOpts): string {
  return buildQuery({
    from: opts.from,
    to: opts.to,
    source: opts.source,
    status: opts.status,
    aging: opts.aging,
    entityId: opts.entityId,
    needsReconciliation: opts.needsReconciliation ? true : undefined,
    page: opts.page,
    perPage: opts.perPage,
  });
}

function buildRefundsQuery(opts: RefundsOpts): string {
  return buildQuery({
    from: opts.from,
    to: opts.to,
    source: opts.source,
    page: opts.page,
    perPage: opts.perPage,
  });
}

// ─── API surface ─────────────────────────────────────────────────────────────

export const adminFinancialApi = {
  /** GET /admin/financial/summary — KPI cards + trend + bySource + funnel + top-N. */
  getSummary: async (opts: SummaryOpts): Promise<FinanceSummary> => {
    const body = await adminFetch<DataEnvelope<FinanceSummary>>(
      `/admin/financial/summary${buildSummaryQuery(opts)}`,
    );
    return body.data;
  },

  /** GET /admin/financial/settlements — commission_statements projection (paginated). */
  listSettlements: async (
    opts: SettlementsOpts,
  ): Promise<FinancePaginated<FinanceSettlementRow>> => {
    return adminFetch<FinancePaginated<FinanceSettlementRow>>(
      `/admin/financial/settlements${buildSettlementsQuery(opts)}`,
    );
  },

  /** GET /admin/financial/refunds — consolidated refunds/reversals UNION (paginated). */
  listRefunds: async (opts: RefundsOpts): Promise<FinancePaginated<FinanceRefundRow>> => {
    return adminFetch<FinancePaginated<FinanceRefundRow>>(
      `/admin/financial/refunds${buildRefundsQuery(opts)}`,
    );
  },

  /** GET /admin/financial/receivables — live AR aging. */
  getReceivables: async (opts?: ReceivablesOpts): Promise<FinanceReceivables> => {
    const body = await adminFetch<DataEnvelope<FinanceReceivables>>(
      `/admin/financial/receivables${buildQuery({ asOf: opts?.asOf })}`,
    );
    return body.data;
  },

  /**
   * POST /admin/financial/refresh — manual rollup recompute. Rate-limited
   * 1/5min/admin (429 FINANCE_REFRESH_RATE_LIMITED on a too-frequent retap).
   */
  refresh: async (): Promise<{ readonly lastRefreshedAt: string }> => {
    const body = await adminFetch<DataEnvelope<{ lastRefreshedAt: string }>>(
      `/admin/financial/refresh`,
      { method: 'POST', body: {} as Record<string, unknown> },
    );
    return body.data;
  },

  /**
   * POST /admin/financial/exports — generate a CSV/Excel export. Requires
   * `financial:export`. Returns the job row (with a 24h signed download URL
   * once status='ready').
   */
  generateExport: async (input: GenerateFinanceExportInput): Promise<FinanceExportJob> => {
    const body = await adminFetch<DataEnvelope<FinanceExportJob>>(`/admin/financial/exports`, {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
    return body.data;
  },

  /** GET /admin/financial/exports — export-history audit trail (paginated). */
  listExportJobs: async (opts?: ExportHistoryOpts): Promise<FinancePaginated<FinanceExportJob>> => {
    return adminFetch<FinancePaginated<FinanceExportJob>>(
      `/admin/financial/exports${buildQuery({ page: opts?.page, perPage: opts?.perPage })}`,
    );
  },

  /** GET /admin/financial/exports/:exportId — status + a freshly-minted signed URL. */
  getExportJob: async (exportId: string): Promise<FinanceExportJob> => {
    const body = await adminFetch<DataEnvelope<FinanceExportJob>>(
      `/admin/financial/exports/${encodeURIComponent(exportId)}`,
    );
    return body.data;
  },

  /** GET /admin/financial/alert-rules — the 4 configurable rules. */
  listAlertRules: async (): Promise<readonly FinanceAlertRule[]> => {
    const body = await adminFetch<DataEnvelope<readonly FinanceAlertRule[]>>(
      `/admin/financial/alert-rules`,
    );
    return body.data;
  },

  /**
   * PATCH /admin/financial/alert-rules/:ruleType — update threshold/enabled/hour.
   * Requires `financial:export` (config changes are sensitive).
   */
  updateAlertRule: async (
    ruleType: FinanceAlertType,
    input: UpdateFinanceAlertRuleInput,
  ): Promise<FinanceAlertRule> => {
    const body = await adminFetch<DataEnvelope<FinanceAlertRule>>(
      `/admin/financial/alert-rules/${encodeURIComponent(ruleType)}`,
      { method: 'PATCH', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },
};
